// @ts-nocheck
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Box } from '@mantine/core';
import { useMantineTheme, useMantineColorScheme } from '@mantine/core';
import { Stack, Center, Container } from '@/components/ui/layout';
import { Card } from '@/components/ui/data-display';
import { Title, Text, Button, Anchor, Badge } from '@/components/ui/core';
import { TextInput, PasswordInput, Checkbox } from '@/components/ui/inputs';
import { IconUser, IconMail, IconLock, IconArrowLeft, IconSparkles } from '@tabler/icons-react';
import { FadeIn, SlideIn, ScaleIn, HoverLift, useReducedMotion } from '@/components/animations';
import { AnimatedFormWrapper, SocialLoginButtons, PasswordStrength } from '../components';

export default function RegisterAnimated() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const reduceMotion = useReducedMotion();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Register attempt:', { name, email, password });
  };

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: isDark
          ? `linear-gradient(135deg, ${theme.colors.dark[8]} 0%, ${theme.colors.dark[9]} 100%)`
          : `linear-gradient(135deg, ${theme.colors.gray[0]} 0%, ${theme.colors.cyan[0]} 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated background elements */}
      <Box style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        {[...Array(6)].map((_, i) => (
          <Box
            key={i}
            style={{
              position: 'absolute',
              width: 80 + i * 40,
              height: 80 + i * 40,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${theme.colors.cyan[isDark ? 8 : 3]}, ${theme.colors.teal[isDark ? 8 : 3]})`,
              opacity: 0.1,
              top: `${5 + i * 15}%`,
              right: `${-5 + i * 10}%`,
              animation: reduceMotion ? 'none' : `float ${4 + i}s ease-in-out infinite`,
              animationDelay: `${i * 0.3}s`,
            }}
          />
        ))}
      </Box>

      <Center mih="100vh" p="md" style={{ position: 'relative', zIndex: 1 }}>
        <Container size="xs" w="100%">
          <ScaleIn delay={100} duration={500}>
            <HoverLift>
              <Card variant="elevated" p="xl" radius="lg">
                <Stack gap="lg">
                  {/* Back button */}
                  <SlideIn direction="left" delay={200}>
                    <Button
                      component={Link}
                      to="/app/demo/auth-pages/register"
                      variant="subtle"
                      size="xs"
                      leftSection={<IconArrowLeft size={14} />}
                      w="fit-content"
                    >
                      Back to variants
                    </Button>
                  </SlideIn>

                  {/* Header */}
                  <FadeIn delay={300}>
                    <Stack align="center" gap="xs">
                      <Badge
                        leftSection={<IconSparkles size={12} />}
                        variant="gradient"
                        gradient={{ from: 'cyan', to: 'teal' }}
                        size="lg"
                      >
                        Animated Experience
                      </Badge>
                      <Title order={2} ta="center" mt="md">
                        Create an account
                      </Title>
                      <Text c="dimmed" size="sm" ta="center">
                        Join us with smooth animations
                      </Text>
                    </Stack>
                  </FadeIn>

                  {/* Form */}
                  <form onSubmit={handleSubmit}>
                    <AnimatedFormWrapper animation="stagger" delay={400} staggerDelay={80}>
                      <TextInput
                        label="Full name"
                        placeholder="John Doe"
                        leftSection={<IconUser size={16} />}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        size="md"
                        radius="md"
                      />

                      <TextInput
                        label="Email"
                        placeholder="your@email.com"
                        leftSection={<IconMail size={16} />}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        size="md"
                        radius="md"
                      />

                      <div>
                        <PasswordInput
                          label="Password"
                          placeholder="Create a password"
                          leftSection={<IconLock size={16} />}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          size="md"
                          radius="md"
                        />
                        {password && <PasswordStrength password={password} variant="bar" />}
                      </div>

                      <PasswordInput
                        label="Confirm password"
                        placeholder="Confirm your password"
                        leftSection={<IconLock size={16} />}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        size="md"
                        radius="md"
                        error={confirmPassword && password !== confirmPassword ? "Passwords don't match" : undefined}
                      />

                      <Checkbox
                        label={
                          <Text size="sm">
                            I agree to the <Anchor size="sm">Terms</Anchor> and{' '}
                            <Anchor size="sm">Privacy Policy</Anchor>
                          </Text>
                        }
                        checked={acceptTerms}
                        onChange={(e) => setAcceptTerms(e.currentTarget.checked)}
                      />

                      <Button
                        type="submit"
                        fullWidth
                        size="md"
                        radius="md"
                        variant="gradient"
                        gradient={{ from: 'cyan', to: 'teal' }}
                        disabled={!acceptTerms}
                      >
                        Create account
                      </Button>
                    </AnimatedFormWrapper>
                  </form>

                  {/* Social Login */}
                  <SlideIn direction="up" delay={900}>
                    <SocialLoginButtons
                      providers={['google', 'github']}
                      layout="horizontal"
                      variant="outline"
                    />
                  </SlideIn>

                  {/* Login Link */}
                  <FadeIn delay={1000}>
                    <Text ta="center" size="sm">
                      Already have an account?{' '}
                      <Anchor component={Link} to="/app/demo/auth-pages/login/animated" fw={500}>
                        Sign in
                      </Anchor>
                    </Text>
                  </FadeIn>
                </Stack>
              </Card>
            </HoverLift>
          </ScaleIn>
        </Container>
      </Center>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
      `}</style>
    </Box>
  );
}
